import type { Class, Sample, TrainedLabel } from '../data/types';

/**
 * Image trainer abstraction.
 *
 * The browser implementation lazy-loads `@tensorflow/tfjs` and
 * `@tensorflow-models/mobilenet` so they stay out of the platform-shell
 * bundle. Tests pass a stub via dependency injection so unit suites never
 * pull TF.js into jsdom.
 */

/**
 * Advanced training knobs the kid can tune from the TrainPanel's
 * "Advanced" section. All optional — trainers fall back to their
 * built-in defaults when a field is missing. Phase 1.5 ships these
 * collapsed by default so the standard TM-parity flow stays a single
 * click. Same shape works for image and audio trainers.
 */
export interface TrainHyperparams {
  epochs?: number;
  batchSize?: number;
  learningRate?: number;
}

export interface TrainInput {
  projectId: string;
  classes: Class[];
  /** All samples for the project, in any order. */
  samples: Sample[];
  hyperparams?: TrainHyperparams;
  onProgress?: (event: TrainProgress) => void;
  signal?: AbortSignal;
}

/** Defaults exposed so the UI can show them in the "Reset to defaults"
 *  button and so test stubs don't need to import the implementation. */
export const DEFAULT_TRAIN_HYPERPARAMS: Required<TrainHyperparams> = {
  epochs: 20,
  batchSize: 16,
  learningRate: 0.001,
};

/** Validation rails — the kid can't ship a 1000-epoch run that bricks
 *  the tab. Min/max chosen to keep training under a minute on a $200
 *  Chromebook for the canonical 5-class × 30-sample fixture. */
export const TRAIN_HYPERPARAM_BOUNDS = {
  epochs: { min: 5, max: 100 },
  batchSize: { min: 1, max: 64 },
  learningRate: { min: 0.0001, max: 0.01 },
};

export function clampHyperparams(input: TrainHyperparams | undefined): Required<TrainHyperparams> {
  const merged = { ...DEFAULT_TRAIN_HYPERPARAMS, ...(input ?? {}) };
  const clamp = (
    v: number,
    { min, max }: { min: number; max: number },
    fallback: number,
  ): number => {
    if (!Number.isFinite(v)) return fallback;
    return Math.max(min, Math.min(max, v));
  };
  return {
    epochs: Math.round(clamp(merged.epochs, TRAIN_HYPERPARAM_BOUNDS.epochs, 20)),
    batchSize: Math.round(clamp(merged.batchSize, TRAIN_HYPERPARAM_BOUNDS.batchSize, 16)),
    learningRate: clamp(merged.learningRate, TRAIN_HYPERPARAM_BOUNDS.learningRate, 0.001),
  };
}

export type TrainProgress =
  | { phase: 'loading-mobilenet' }
  | { phase: 'extracting'; current: number; total: number }
  | {
      phase: 'training';
      epoch: number;
      epochs: number;
      loss: number;
      accuracy: number;
    };

export interface TrainResult {
  labels: TrainedLabel[];
  sampleCounts: Record<string, number>;
  /** Per-epoch loss recorded during fit, oldest first. Populated by the
   * trainer; the UI uses it to render the loss curve and to make the
   * convergence verdict reproducible. */
  lossHistory: number[];
  /** Loss at the last epoch. */
  finalLoss: number;
  /** Training-set accuracy at the last epoch. NOT held-out — eval covers
   * that — but useful for showing the kid how well training itself went. */
  finalAccuracy: number;
  /** Whether the loss curve flattened by the final epoch. False means the
   * model was still meaningfully improving when training stopped — i.e.
   * "still learning". */
  converged: boolean;
}

/**
 * Opaque handle to a loaded classifier. The browser implementation packs a
 * TF.js LayersModel + the MobileNet feature extractor; tests pass anything
 * that survives a round-trip through `predict` and `disposeModel`.
 */
export type LoadedModel = unknown;

/**
 * Anything `tf.browser.fromPixels` (and `createImageBitmap`) accepts. The
 * predict panel uses a video element for live webcam inference and an
 * image element for upload-to-test.
 */
export type PredictSource = HTMLVideoElement | HTMLImageElement | HTMLCanvasElement | ImageBitmap;

export interface ExportModelInput {
  projectId: string;
  /** Used to derive a kid-friendly download filename. */
  projectName: string;
  /** Label order matches the trained model's output indices. */
  labels: TrainedLabel[];
}

/**
 * Raw artefacts that come out of TF.js's save handler. The PWA export
 * pipeline (Phase 2 M3) needs the unpacked model.json + weights.bin
 * bytes, not the legacy single-zip download. Trainers that don't yet
 * implement this can omit it; the PWA exporter checks for it before
 * calling.
 */
export interface ModelArtifacts {
  /** Parsed `model.json`, ready to JSON.stringify back out. */
  modelJson: unknown;
  /** Concatenated weights bytes — written verbatim into the zip. */
  weightsBin: Uint8Array;
  /** Bryta-specific sidecar metadata. The legacy single-zip export
   *  also writes this as `metadata.json`. */
  metadataJson: {
    brytaProjectName: string;
    brytaLabels: TrainedLabel[];
    brytaSchemaVersion: 1;
    exportedAt: string;
    brytaModelKind?: string;
  };
}

export interface ImageTrainer {
  train(input: TrainInput): Promise<TrainResult>;
  /** Removes the persisted classifier weights for a project. Idempotent. */
  removeModel(projectId: string): Promise<void>;
  /** Loads a previously-trained model. Returns null if none exists. */
  loadModel(projectId: string): Promise<LoadedModel | null>;
  /**
   * Runs a single inference on `source`. Accepts a video element (live
   * webcam frame) or any of the other still-image surfaces. Returns one
   * probability per output class, in the order the model was trained on.
   */
  predict(model: LoadedModel, source: PredictSource): Promise<number[]>;
  /** Frees any GPU/CPU resources held by a loaded model. Idempotent. */
  disposeModel(model: LoadedModel): void;
  /**
   * Triggers the browser to download the trained classifier as TF.js
   * artifacts (model.json + weights.bin). The labels are embedded in
   * `userDefinedMetadata` so the kid (or downstream tooling) can map output
   * indices back to class names without a separate file. Resolves once the
   * downloads have been initiated.
   */
  exportModel(input: ExportModelInput): Promise<void>;
  /**
   * Returns the raw TF.js artefacts for this project — the same bytes
   * `exportModel` would zip into a download, but unwrapped so callers
   * (Phase 2 M3 PWA exporter) can lay them out differently. Optional
   * because audio + future trainers may not need it for v1.
   */
  extractArtifacts?(input: ExportModelInput): Promise<ModelArtifacts>;
}

/** Build a kid-friendly filename stem from a project name. */
export function buildExportFilename(projectName: string): string {
  const trimmed = projectName.trim().toLowerCase();
  const slug = trimmed.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug ? `bryta-${slug}` : 'bryta-model';
}

/** Sentinel error a trainer throws when its input is unusable. */
export class TrainingInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TrainingInputError';
  }
}

export const MIN_CLASSES = 2;
export const MIN_SAMPLES_PER_CLASS = 5;

/** How many recent epochs we look at when deciding convergence. */
export const CONVERGENCE_WINDOW = 5;
/** Relative loss-improvement threshold over the convergence window.
 * If `(loss[n - WINDOW] - loss[n]) / loss[n - WINDOW]` is below this, the
 * curve has flattened enough to call it converged. */
export const CONVERGENCE_REL_IMPROVEMENT = 0.05;

export interface ConvergenceVerdict {
  converged: boolean;
  /** Machine-readable reason. The UI maps these to kid-friendly copy. */
  reason: 'plateaued' | 'flat-at-zero' | 'too-few-epochs' | 'still-improving';
}

/**
 * Decide whether a training run's loss curve has flattened.
 *
 * Used by the trainer at the end of `fit()` and verified by Phase 1 gate
 * test #7 ("Training is not declared 'ready' until loss has stabilized").
 *
 * Pure function — no TF.js, jsdom-friendly, the same logic runs in the
 * browser and in unit tests.
 */
export function checkConvergence(lossHistory: number[]): ConvergenceVerdict {
  if (lossHistory.length < CONVERGENCE_WINDOW) {
    return { converged: false, reason: 'too-few-epochs' };
  }
  const earlier = lossHistory[lossHistory.length - CONVERGENCE_WINDOW];
  const final = lossHistory[lossHistory.length - 1];
  if (earlier === undefined || final === undefined) {
    return { converged: false, reason: 'too-few-epochs' };
  }
  // A loss already at zero (or very close) is converged by definition —
  // there's nothing left to learn.
  if (earlier <= 1e-6 && final <= 1e-6) {
    return { converged: true, reason: 'flat-at-zero' };
  }
  if (earlier <= 0) {
    return { converged: true, reason: 'flat-at-zero' };
  }
  const relImprovement = (earlier - final) / earlier;
  if (relImprovement < CONVERGENCE_REL_IMPROVEMENT) {
    return { converged: true, reason: 'plateaued' };
  }
  return { converged: false, reason: 'still-improving' };
}

/** Pure helper used by both UI and tests to decide if we have enough data. */
export function checkTrainability(
  classes: Pick<Class, 'id'>[],
  samples: Pick<Sample, 'classId'>[],
): { ok: true } | { ok: false; reason: string } {
  if (classes.length < MIN_CLASSES) {
    return {
      ok: false,
      reason: `You need at least ${MIN_CLASSES} classes to train.`,
    };
  }
  const counts = new Map<string, number>();
  for (const sample of samples) {
    counts.set(sample.classId, (counts.get(sample.classId) ?? 0) + 1);
  }
  for (const cls of classes) {
    const count = counts.get(cls.id) ?? 0;
    if (count < MIN_SAMPLES_PER_CLASS) {
      return {
        ok: false,
        reason: `Each class needs at least ${MIN_SAMPLES_PER_CLASS} samples. One of yours has ${count}.`,
      };
    }
  }
  return { ok: true };
}
